import {
  Archive,
  BookOpen,
  ClipboardList,
  FileSpreadsheet,
  History,
  PackageCheck,
  PlusCircle,
  TriangleAlert,
  type LucideIcon,
} from "lucide-react";
import type { CaseCounts } from "../../../shared/contracts.js";
import type { CaseListView, CaseStatus } from "../../../shared/domain.js";
import {
  STATUS_PRESENTATION,
  resolveStatusTone,
  type StatusPresentation,
  type StatusTone,
} from "../../ui/StatusBadge.js";

/**
 * Presentation vocabulary for the case workspaces: destination names and
 * icons, status labels and icons, and the small label formatters rows and
 * counts share (spec §4, §10).
 *
 * Everything here is *presentation* of a server decision. Status, list
 * membership, permissions and the 48-hour window are never calculated in the
 * client; these helpers only turn the server's values into words and icons.
 */

// ---------------------------------------------------------------------------
// Status
// ---------------------------------------------------------------------------

/**
 * Status labels and icons. The single definition lives with the badge
 * primitive (`ui/StatusBadge.tsx`, Task 9) so the badge cannot drift from
 * the workspace copy; it is re-exported here as the feature's vocabulary.
 */
export { STATUS_PRESENTATION, resolveStatusTone };
export type { StatusPresentation, StatusTone };

/** The one presentation to show for a case: attention outranks the normal status. */
export function statusPresentation(
  status: CaseStatus,
  needsAttention: boolean,
): StatusPresentation & { tone: StatusTone } {
  const tone = resolveStatusTone(status, needsAttention);
  return { ...STATUS_PRESENTATION[tone], tone };
}

// ---------------------------------------------------------------------------
// Parts destinations (spec §10 "Left navigation")
// ---------------------------------------------------------------------------

/** The five list views the Parts workspace shows (the shared views belong to Task 13). */
export const PARTS_VIEWS = [
  "work_queue",
  "arrived_48h",
  "needs_attention",
  "all",
  "history",
] as const satisfies readonly CaseListView[];

export type PartsView = (typeof PARTS_VIEWS)[number];

export const DEFAULT_PARTS_VIEW: PartsView = "work_queue";

export function isPartsView(value: unknown): value is PartsView {
  return (
    typeof value === "string" &&
    (PARTS_VIEWS as readonly string[]).includes(value)
  );
}

export type PartsViewPresentation = Readonly<{
  label: string;
  Icon: LucideIcon;
  /** One line under the list heading saying what the view contains. */
  description: string;
  /** Plain-language empty state when the server returns no rows. */
  emptyTitle: string;
  emptyDescription: string;
}>;

export const PARTS_VIEW_PRESENTATION: Readonly<
  Record<PartsView, PartsViewPresentation>
> = {
  work_queue: {
    label: "Work queue",
    Icon: ClipboardList,
    description:
      "Open orders, anything needing attention, and arrivals from the last 48 hours.",
    emptyTitle: "Nothing in the work queue",
    emptyDescription:
      "Every order has arrived and left its 48-hour window. New orders appear here as soon as they are created.",
  },
  arrived_48h: {
    label: "Arrived · 48h",
    Icon: PackageCheck,
    description: "Orders that arrived within the last 48 hours.",
    emptyTitle: "No recent arrivals",
    emptyDescription:
      "Orders show here for 48 hours after every part is verified, then move to History.",
  },
  needs_attention: {
    label: "Needs attention",
    Icon: TriangleAlert,
    description:
      "Orders with a backordered, wrong, damaged or cancelled part line.",
    emptyTitle: "Nothing needs attention",
    emptyDescription:
      "No open order has a backordered, wrong, damaged or cancelled line.",
  },
  all: {
    label: "All orders",
    Icon: Archive,
    description: "Every order, active and historical.",
    emptyTitle: "No orders yet",
    emptyDescription: "Orders appear here as soon as they are created.",
  },
  history: {
    label: "History",
    Icon: History,
    description:
      "Cancelled orders and arrivals older than 48 hours. Still searchable, never deleted.",
    emptyTitle: "No history yet",
    emptyDescription:
      "Cancelled orders and arrivals older than 48 hours will be listed here.",
  },
};

export type PartsDestination =
  | Readonly<{
      kind: "view";
      view: PartsView;
      label: string;
      Icon: LucideIcon;
      /** Which centre-list count, if any, this destination summarises. */
      countKey: keyof CaseCounts | null;
    }>
  | Readonly<{
      kind: "new_order";
      label: string;
      Icon: LucideIcon;
      to: string;
    }>
  | Readonly<{
      /** The stock order report (owner request 2026-09-17): a page, not a list. */
      kind: "stock_order_report";
      label: string;
      Icon: LucideIcon;
      to: string;
    }>
  | Readonly<{
      /** The parts catalog (spec §6.3): a page, not a list. */
      kind: "catalog";
      label: string;
      Icon: LucideIcon;
      to: string;
    }>;

/** The page destinations that are not lists; each marks its own link current. */
export type PartsPageDestination = "new_order" | "stock_order_report" | "catalog";

export const NEW_ORDER_PATH = "/parts/new";

export const STOCK_ORDER_REPORT_PATH = "/parts/reports/stock-orders";

export const CATALOG_PATH = "/parts/catalog";

/** The six destinations in the order the spec lists them, then the stock order report and the catalog. */
export const PARTS_DESTINATIONS: readonly PartsDestination[] = [
  { kind: "new_order", label: "New order", Icon: PlusCircle, to: NEW_ORDER_PATH },
  ...PARTS_VIEWS.map<PartsDestination>((view) => ({
    kind: "view",
    view,
    label: PARTS_VIEW_PRESENTATION[view].label,
    Icon: PARTS_VIEW_PRESENTATION[view].Icon,
    countKey:
      view === "needs_attention"
        ? "needsAttention"
        : view === "arrived_48h"
          ? "arrived"
          : null,
  })),
  {
    kind: "stock_order_report",
    label: "Stock order report",
    Icon: FileSpreadsheet,
    to: STOCK_ORDER_REPORT_PATH,
  },
  { kind: "catalog", label: "Parts catalog", Icon: BookOpen, to: CATALOG_PATH },
];

// ---------------------------------------------------------------------------
// Centre-list counts (spec §10 "Center list")
// ---------------------------------------------------------------------------

export type CountPresentation = Readonly<{
  key: keyof CaseCounts;
  label: string;
  tone: StatusTone;
  /** The view that lists exactly these cases, when one exists. */
  view: PartsView | null;
}>;

export const COUNT_PRESENTATION: readonly CountPresentation[] = [
  {
    key: "needsAttention",
    label: "Needs attention",
    tone: "needs_attention",
    view: "needs_attention",
  },
  {
    key: "partiallyArrived",
    label: "Partially arrived",
    tone: "partially_arrived",
    view: null,
  },
  { key: "arrived", label: "Arrived", tone: "arrived", view: "arrived_48h" },
];

// ---------------------------------------------------------------------------
// Row and detail labels
// ---------------------------------------------------------------------------

/** "RO 48213", or "Pre-RO" while the case has no repair order yet. */
export function roLabel(roNumber: string | null): string {
  return roNumber ? `RO ${roNumber}` : "Pre-RO";
}

/** The VIN ending, or "—" while the order has no VIN (optional since 2026-09-17). */
export function vinEndingLabel(vinEnding: string | null): string {
  return vinEnding ?? "—";
}

/** The technician's name, or "Unassigned" while the order has none (optional since 2026-09-17). */
export function technicianLabel(technicianName: string | null): string {
  return technicianName ?? "Unassigned";
}

/** "3 of 8 verified" — the server's totals, never recomputed from lines. */
export function progressLabel(
  quantityVerified: number,
  quantityOrdered: number,
): string {
  return `${quantityVerified} of ${quantityOrdered} verified`;
}

/** 0–100 for a progress bar; an order with nothing ordered reads as 0. */
export function progressPercent(
  quantityVerified: number,
  quantityOrdered: number,
): number {
  if (quantityOrdered <= 0) return 0;
  return Math.max(
    0,
    Math.min(100, Math.round((quantityVerified / quantityOrdered) * 100)),
  );
}

const shortInstantFormatter = new Intl.DateTimeFormat("en-US", {
  timeZone: "America/Chicago",
  month: "short",
  day: "numeric",
  hour: "numeric",
  minute: "2-digit",
});

/** "Sep 16, 8:05 AM" in the dealership's timezone; an unparseable value is shown as-is. */
export function formatShortInstant(iso: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? iso : shortInstantFormatter.format(date);
}

const countFormatter = new Intl.NumberFormat("en-US");

export function formatCount(value: number): string {
  return countFormatter.format(value);
}
